import { Menu, Sun, Moon, Monitor, Globe, LogOut, ChevronDown, Building2, Search, CircleHelp } from "lucide-react";
import { useTheme } from "next-themes";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";

export default function Topbar({ onOpenMobile, onOpenSearch, onOpenHelp }) {
  const { theme, setTheme } = useTheme();
  const { t, locale, setLocale } = useI18n();
  const { user, logout, memberships, activeTenantId, switchTenant, updatePreferences } = useAuth();

  const activeTenant = memberships.find((m) => m.tenant_id === activeTenantId);

  const changeTheme = (val) => {
    setTheme(val);
    updatePreferences({ theme: val }).catch(() => {});
  };
  const changeLocale = (val) => {
    setLocale(val);
    updatePreferences({ locale: val }).catch(() => {});
  };

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/80 px-4 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/80 sm:px-6 lg:px-8">
      <button className="lg:hidden" onClick={onOpenMobile} data-testid="open-mobile-nav" aria-label="Menu">
        <Menu className="h-5 w-5" />
      </button>

      {activeTenant && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Building2 className="h-4 w-4" />
          <span className="hidden font-medium text-slate-700 dark:text-slate-300 sm:inline" data-testid="active-tenant-name">
            {activeTenant.tenant_name}
          </span>
        </div>
      )}

      {onOpenSearch && <button
        onClick={onOpenSearch}
        data-testid="topbar-search"
        className="ml-auto flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-400 transition-colors hover:border-slate-300 hover:text-slate-600 dark:border-slate-800 dark:bg-slate-800/50 dark:hover:border-slate-700"
        aria-label={t("search.placeholder")}
      >
        <Search className="h-4 w-4" />
        <span className="hidden sm:inline">{t("search.placeholder")}</span>
        <kbd className="ml-2 hidden rounded border border-slate-300 px-1.5 font-mono text-[10px] text-slate-400 dark:border-slate-600 sm:inline">⌘K</kbd>
      </button>}

      <div className={`${onOpenSearch ? "ml-2" : "ml-auto"} flex items-center gap-1.5`}>
        <Button
          variant="ghost"
          size="icon"
          onClick={onOpenHelp}
          data-testid="topbar-help"
          aria-label={t("help.open")}
          title={t("help.open")}
        >
          <CircleHelp className="h-4 w-4" />
        </Button>

        {/* Language */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" data-testid="language-switcher" className="gap-1.5">
              <Globe className="h-4 w-4" />
              <span className="uppercase">{locale}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuRadioGroup value={locale} onValueChange={changeLocale}>
              <DropdownMenuRadioItem value="de" data-testid="lang-de">{t("settings.german")}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="en" data-testid="lang-en">{t("settings.english")}</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Theme */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="theme-switcher" aria-label={t("settings.theme")}>
              <Sun className="h-4 w-4 dark:hidden" />
              <Moon className="hidden h-4 w-4 dark:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuRadioGroup value={theme} onValueChange={changeTheme}>
              <DropdownMenuRadioItem value="light" data-testid="theme-light"><Sun className="mr-2 h-4 w-4" />{t("settings.theme_light")}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark" data-testid="theme-dark"><Moon className="mr-2 h-4 w-4" />{t("settings.theme_dark")}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="system" data-testid="theme-system"><Monitor className="mr-2 h-4 w-4" />{t("settings.theme_system")}</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" data-testid="user-menu" className="gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white dark:bg-white dark:text-slate-900">
                {(user?.full_name || "?").charAt(0).toUpperCase()}
              </div>
              <span className="hidden max-w-[140px] truncate text-sm sm:inline">{user?.full_name}</span>
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
            {memberships.length > 1 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-slate-400">{t("auth.select_company")}</DropdownMenuLabel>
                {memberships.map((m) => (
                  <DropdownMenuItem
                    key={m.tenant_id}
                    onClick={() => switchTenant(m.tenant_id)}
                    data-testid={`switch-tenant-${m.tenant_id}`}
                    className={m.tenant_id === activeTenantId ? "font-semibold" : ""}
                  >
                    {m.tenant_name}
                  </DropdownMenuItem>
                ))}
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="logout-button">
              <LogOut className="mr-2 h-4 w-4" />{t("auth.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
